import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, ChevronRight, Sparkles } from 'lucide-react';
import { BUSINESS_PRICE_USD, nextResetDate } from '../../lib/mockPlan';

/**
 * UpgradeModal — frontend-only soft/hard gating modal for Talent Connect.
 *
 * Stripe is NOT wired; the "Upgrade to Business" CTA routes to /talent-connect/pricing
 * (which itself shows a "Coming soon — Stripe in progress" hint).
 *
 * variant:
 *   'hard' → user tried to do something blocked; Maybe-later closes without
 *            executing the underlying action
 *   'soft' → informational; clicking outside / Maybe-later is harmless
 *
 * feature decides headline + body copy + comparison strip.
 */
const FEATURE_COPY = {
  rooms_limit: {
    title: "You've reached your monthly limit",
    body: () => (
      <>
        You've used <strong>3 of 3</strong> free interview rooms this month. Your
        limit resets on <strong>{nextResetDate()}</strong>, or upgrade to Business
        for unlimited rooms.
      </>
    ),
    compare: { free: '3 rooms / month', business: 'Unlimited rooms' },
  },
  analytics: {
    title: 'Analytics is a Business feature',
    body: () => (
      <>
        See pass-rates per question, candidate funnel metrics, and Indiana-specific
        KPIs. Available on the Business plan.
      </>
    ),
    compare: { free: 'Result screen only', business: 'Full analytics dashboard' },
  },
  export: {
    title: 'Export is a Business feature',
    body: () => (
      <>Download interview results as CSV or PDF for your records. Available on the Business plan.</>
    ),
    compare: { free: 'No export', business: 'CSV + PDF export' },
  },
  custom_questions: {
    title: 'Custom questions require Business',
    body: () => (
      <>
        Replace the 3 default Indiana questions with up to 10 of your own.
        Available on the Business plan.
      </>
    ),
    compare: { free: '3 fixed questions', business: 'Up to 10 custom' },
  },
  branding: {
    title: 'Custom branding is a Business feature',
    body: () => (
      <>Add your company logo and customize colors. Available on the Business plan.</>
    ),
    compare: { free: 'SignVoice branding', business: 'Your logo + colors' },
  },
};

export default function UpgradeModal({ isOpen, onClose, feature = 'rooms_limit', variant = 'soft' }) {
  const navigate = useNavigate();
  const copy = FEATURE_COPY[feature] || FEATURE_COPY.rooms_limit;

  // Lock body scroll while open + close on Escape (a11y).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose?.();
    navigate('/talent-connect/pricing#business');
  };

  return (
    <div
      className="tc-modal-backdrop"
      data-testid="upgrade-modal-backdrop"
      data-variant={variant}
      onClick={onClose}
    >
      <div
        className="tc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        data-testid="upgrade-modal"
        data-feature={feature}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="tc-modal-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="upgrade-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="tc-modal-icon">
          <Lock className="w-7 h-7" strokeWidth={2.2} />
        </div>

        <h2 id="upgrade-modal-title" className="tc-modal-title" data-testid="upgrade-modal-title">
          {copy.title}
        </h2>
        <p className="tc-modal-body" data-testid="upgrade-modal-body">
          {copy.body()}
        </p>

        {copy.compare && (
          <div className="tc-modal-compare" data-testid="upgrade-modal-compare">
            <div className="tc-modal-compare-col">
              <span className="tc-modal-compare-label">Free</span>
              <span className="tc-modal-compare-val">{copy.compare.free}</span>
            </div>
            <div className="tc-modal-compare-arrow">→</div>
            <div className="tc-modal-compare-col tc-modal-compare-col--biz">
              <span className="tc-modal-compare-label">Business</span>
              <span className="tc-modal-compare-val">{copy.compare.business}</span>
            </div>
          </div>
        )}

        <div className="tc-modal-cta">
          <button
            type="button"
            className="tc-btn tc-btn--primary tc-btn--block"
            onClick={handleUpgrade}
            data-testid="upgrade-modal-cta-upgrade"
          >
            <Sparkles className="w-4 h-4" />
            <span>Upgrade to Business — ${BUSINESS_PRICE_USD}.00/month</span>
          </button>
          <button
            type="button"
            className="tc-btn tc-btn--ghost tc-btn--block"
            onClick={onClose}
            data-testid="upgrade-modal-cta-later"
          >
            Maybe later
          </button>
        </div>

        <button
          type="button"
          className="tc-modal-link"
          onClick={handleUpgrade}
          data-testid="upgrade-modal-view-plans"
        >
          View all plans <ChevronRight className="w-3 h-3" />
        </button>

        <p className="tc-modal-trust" data-testid="upgrade-modal-trust">
          <strong style={{ color: '#10B981' }}>30-day free trial</strong> · Cancel anytime · 30-day money-back guarantee
        </p>
      </div>
    </div>
  );
}
